import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logoImage from '../assets/logo.png';
import LanguageSwitcher from './LanguageSwitcher';

const HeaderWrapper = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  width: 100%;
  /* Always use the scrolled background */
  background: rgba(5, 12, 24, 0.85);
  backdrop-filter: saturate(180%) blur(10px);
  z-index: 1000;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  transition: background 0.25s ease, transform 0.2s ease, box-shadow 0.25s ease;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
`;

const Nav = styled.nav<{ $scrolled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Always use the compact (scrolled) padding */
  padding: 0.6rem 0;
  max-width: 1200px;
  margin: 0 auto;
  padding-left: 20px;
  padding-right: 20px;

  @media (max-width: 768px) {
    /* Ensure comfortable tappable height and vertical centering on mobile */
    min-height: 56px;
  }
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const LogoImage = styled.img`
  height: 40px;
  width: auto;
  display: block; /* avoid baseline alignment gap */
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    /* Slightly smaller logo on mobile for better balance with burger */
    height: 32px;
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const NavMenu = styled.ul<{ isOpen: boolean }>`
  display: flex;
  list-style: none;
  gap: 2rem;
  margin: 0;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    height: calc(100vh - 70px);
    background: rgba(5, 12, 24, 0.9);
    flex-direction: column;
    justify-content: flex-start;
    padding: 2rem;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
    box-shadow: ${props => props.isOpen ? '0 4px 20px rgba(0, 0, 0, 0.3)' : 'none'};
  }
`;

const NavItem = styled.li``;

const NavLinkStyled = styled(Link)<{ $active?: boolean }>`
  position: relative;
  color: ${(p) => (p.$active ? 'var(--primary-orange)' : 'white')};
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
  padding: 0.25rem 0;

  &:hover {
    color: var(--primary-orange);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    width: ${(p) => (p.$active ? '100%' : '0%')};
    height: 2px;
    background: linear-gradient(90deg, var(--primary-orange), #ff7043);
    transition: width 200ms ease;
  }

  &:hover::after { width: 100%; }
`;

// External link style matching NavLinkStyled (no active state)
const ExternalNavLink = styled.a`
  position: relative;
  color: white;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
  padding: 0.25rem 0;

  &:hover {
    color: var(--primary-orange);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -4px;
    width: 0%;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-orange), #ff7043);
    transition: width 200ms ease;
  }

  &:hover::after { width: 100%; }
`;


// No portal login/profile controls in public-only header

const MobileMenuBtn = styled.button`
  display: none;
  background: transparent;
  border: none;
  font-size: 1.5rem;
  color: white;
  padding: 0; /* use fixed touch target instead */
  line-height: 1; /* avoid extra vertical space inside button */
  
  @media (max-width: 768px) {
    /* Make the burger a square touch target and perfectly centered */
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    /* Nudge the glyph slightly upward without moving the touch target */
    padding-bottom: 2px;
  }
`;

const Backdrop = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.35);
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  pointer-events: ${(p) => (p.$visible ? 'auto' : 'none')};
  transition: opacity 200ms ease;
  z-index: 900; /* below header */
`;

// Avatar and portal profile elements removed

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
    <HeaderWrapper $scrolled={scrolled}>
      <Nav $scrolled={scrolled} aria-label="Main navigation">
        <LogoLink to="/">
            <LogoImage
                src={logoImage}
                alt="UniversalDrive Logo"
            />
        </LogoLink>
        
        <NavMenu isOpen={isMenuOpen} role="menubar" aria-hidden={!isMenuOpen}>
          <NavItem role="none"><NavLinkStyled role="menuitem" to="/" onClick={closeMenu} $active={location.pathname === '/'}>{t('nav.home')}</NavLinkStyled></NavItem>
          <NavItem role="none"><NavLinkStyled role="menuitem" to="/auto" onClick={closeMenu} $active={location.pathname.startsWith('/auto')}>{t('nav.auto')}</NavLinkStyled></NavItem>
          <NavItem role="none"><NavLinkStyled role="menuitem" to="/tarieven" onClick={closeMenu} $active={location.pathname.startsWith('/tarieven')}>{t('nav.tarieven')}</NavLinkStyled></NavItem>
          <NavItem role="none">
            <ExternalNavLink
              role="menuitem"
              href="https://detheorieprofessor.nl/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
            >
              {t('nav.theoriecursus')}
            </ExternalNavLink>
          </NavItem>
          
          <NavItem>
            <LanguageSwitcher onLanguageChange={closeMenu} />
          </NavItem>
        </NavMenu>
        
        <MobileMenuBtn onClick={toggleMenu} aria-expanded={isMenuOpen} aria-controls="site-menu" aria-label="Toggle menu">
          ☰
        </MobileMenuBtn>
      </Nav>
    </HeaderWrapper>
    <Backdrop $visible={isMenuOpen} onClick={closeMenu} aria-hidden={!isMenuOpen} />
    </>
  );
};

export default Header;